



import React, { useEffect, useRef, useState, Children, cloneElement, isValidElement } from 'react';

// Reveal — wraps a block of content (usually a heading group) and animates
// its direct children in with a staggered slide-up, same feel as the hero.
// Unlike a typical "reveal once" pattern, this RESETS when the element
// leaves the viewport, so it replays every time you scroll it back into view.
export default function Reveal({
  children,
  className = '',
  direction = 'up', // 'up' | 'left' | 'right'
  delay = 0,        // base delay in ms before the first child starts
  stagger = 120,    // ms gap between each direct child's start
  threshold = 0.2,  // how much of the element must be visible to trigger
}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setInView(entry.isIntersecting);
      },
      { threshold }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  const baseTransform =
    direction === 'left'
      ? 'translateX(-24px)'
      : direction === 'right'
      ? 'translateX(24px)'
      : 'translateY(28px)';

  const childArray = Children.toArray(children);

  return (
    <div ref={ref} className={className}>
      {childArray.map((child, i) => {
        const childDelay = delay + i * stagger;
        const revealStyle = {
          transition: `opacity 0.7s cubic-bezier(0.22,1,0.36,1) ${childDelay}ms, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${childDelay}ms`,
          opacity: inView ? 1 : 0,
          transform: inView ? 'translate(0, 0)' : baseTransform,
          willChange: 'opacity, transform',
        };

        if (isValidElement(child)) {
          return cloneElement(child, {
            key: i,
            style: { ...(child.props.style || {}), ...revealStyle },
          });
        }

        return (
          <span key={i} style={revealStyle}>
            {child}
          </span>
        );
      })}
    </div>
  );
}

